/**
 * Formatter Utilities
 * Centralized formatting functions for dates, numbers, currency, etc.
 *
 * @module formatters
 * @version 2.0.0
 *
 * TIMEZONE-AWARE FORMATTING:
 * For timezone-aware date formatting, prefer using the useTimezone() hook from TimezoneContext.
 * The hook provides formatters that automatically use the organization's timezone.
 *
 * Example with useTimezone():
 *   const { formatDate, formatDateTime } = useTimezone();
 *   <span>{formatDate(row.createdAt)}</span>
 *
 * For standalone usage (outside React components), use the timezone-aware functions
 * exported from this module with explicit timezone parameter:
 *   formatDateWithTimezone(date, 'Asia/Kolkata')
 *
 * LEGACY USAGE IN TABLES (uses browser timezone):
 * Use formatDateShort() for consistent DD/MM/YYYY date formatting across all tables.
 * Example in column config:
 *   dateColumn: {
 *     label: 'Date',
 *     enableSorting: true,
 *     enableColumnFilter: true,
 *     filterType: 'date',
 *     render: (row: any) => formatDateShort(row.dateField),
 *   }
 */

import { format } from 'date-fns';
import { toZonedTime, fromZonedTime, formatInTimeZone } from 'date-fns-tz';

/**
 * Format date to localized string
 * @param date - Date string or Date object
 * @param locale - Locale code (default: 'en-IN')
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export const formatDate = (
  date: string | Date,
  locale: string = 'en-IN',
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return '';

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };

  return new Intl.DateTimeFormat(locale, options || defaultOptions).format(dateObj);
};

/**
 * Format date to DD/MM/YYYY
 */
export const formatDateShort = (date: string | Date): string => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return '';

  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();

  return `${day}/${month}/${year}`;
};

/**
 * Format date to YYYY-MM-DD (for input fields)
 */
export const formatDateISO = (date: string | Date): string => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return '';

  return dateObj.toISOString().split('T')[0];
};

/**
 * Format time to HH:MM AM/PM
 * @param time - Time string or Date object
 * @returns Formatted time string in local timezone
 */
export const formatTime = (time: string | Date): string => {
  if (!time) return '';

  const dateObj = typeof time === 'string' ? new Date(time) : time;

  if (isNaN(dateObj.getTime())) return '';

  return new Intl.DateTimeFormat('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(dateObj);
};

/**
 * Format time to 24-hour format HH:MM
 */
export const formatTime24 = (time: string | Date): string => {
  if (!time) return '';

  const dateObj = typeof time === 'string' ? new Date(time) : time;

  if (isNaN(dateObj.getTime())) return '';

  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
};

/**
 * Format date and time together
 * @param dateTime - DateTime string or Date object
 * @returns Formatted date-time string in local timezone
 */
export const formatDateTime = (dateTime: string | Date): string => {
  if (!dateTime) return '';

  const dateObj = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;

  if (isNaN(dateObj.getTime())) return '';

  return new Intl.DateTimeFormat('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(dateObj);
};

/**
 * Format date-time with seconds
 */
export const formatDateTimeFull = (dateTime: string | Date): string => {
  if (!dateTime) return '';

  const dateObj = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;

  if (isNaN(dateObj.getTime())) return '';

  return new Intl.DateTimeFormat('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).format(dateObj);
};

/**
 * Format currency to INR
 * @param amount - Amount to format
 * @param currency - Currency code (default: 'INR')
 * @param locale - Locale code (default: 'en-IN')
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number,
  currency: string = 'INR',
  locale: string = 'en-IN'
): string => {
  if (amount === null || amount === undefined) return '₹0.00';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Format currency without decimals
 */
export const formatCurrencyRounded = (
  amount: number,
  currency: string = 'INR',
  locale: string = 'en-IN'
): string => {
  if (amount === null || amount === undefined) return '₹0';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Format number with thousand separators
 * @param num - Number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted number string
 */
export const formatNumber = (num: number, decimals: number = 2): string => {
  if (num === null || num === undefined) return '0';

  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
};

/**
 * Format number to compact notation (K, M, B)
 * @param num - Number to format
 * @returns Compact number string
 */
export const formatNumberCompact = (num: number): string => {
  if (num === null || num === undefined) return '0';

  return new Intl.NumberFormat('en-IN', {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(num);
};

/**
 * Format distance from meters
 * @param meters - Distance in meters
 * @returns Formatted distance string with units
 */
export const formatDistance = (meters: number): string => {
  if (meters === null || meters === undefined) return '0 m';

  if (meters < 1000) {
    return `${Math.round(meters)} m`;
  }

  const kilometers = meters / 1000;
  return `${kilometers.toFixed(1)} km`;
};

/**
 * Format duration from minutes
 * @param minutes - Duration in minutes
 * @returns Formatted duration string (e.g., "2h 30m" or "45m")
 */
export const formatDuration = (minutes: number): string => {
  if (minutes === null || minutes === undefined || minutes === 0) return '0m';

  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);

  if (hours === 0) {
    return `${mins}m`;
  }

  if (mins === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${mins}m`;
};

/**
 * Format duration from seconds
 * @param seconds - Duration in seconds
 * @returns Formatted duration string (e.g., "1h 30m 45s")
 */
export const formatDurationFromSeconds = (seconds: number): string => {
  if (seconds === null || seconds === undefined || seconds === 0) return '0s';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.round(seconds % 60);

  const parts: string[] = [];

  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(' ');
};

/**
 * Format percentage
 * @param value - Percentage value (0-100 or 0-1 with fraction=true)
 * @param decimals - Number of decimal places (default: 1)
 * @param fraction - If true, input is 0-1, else 0-100 (default: false)
 * @returns Formatted percentage string
 */
export const formatPercentage = (
  value: number,
  decimals: number = 1,
  fraction: boolean = false
): string => {
  if (value === null || value === undefined) return '0%';

  const percentage = fraction ? value * 100 : value;

  return `${percentage.toFixed(decimals)}%`;
};

/**
 * Format file size from bytes
 * @param bytes - File size in bytes
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) return '0 Bytes';
  if (!bytes) return '';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
};

/**
 * Format phone number (Indian format)
 * @param phone - Phone number string
 * @returns Formatted phone number
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return '';

  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');

  // Format as +91 XXXXX XXXXX or XXXXX XXXXX
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  }

  if (cleaned.length === 12 && cleaned.startsWith('91')) {
    return `+91 ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
  }

  return phone; // Return original if format doesn't match
};

/**
 * Format relative time (e.g., "2 hours ago", "3 days ago")
 * @param date - Date string or Date object
 * @returns Relative time string
 */
export const formatRelativeTime = (date: string | Date): string => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return '';

  const now = new Date();
  const diff = now.getTime() - dateObj.getTime();

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (seconds < 60) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  if (weeks < 4) return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  return `${years} year${years === 1 ? '' : 's'} ago`;
};

/**
 * Format month and year (e.g., "January 2025")
 */
export const formatMonthYear = (date: string | Date): string => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return '';

  return new Intl.DateTimeFormat('en-IN', {
    year: 'numeric',
    month: 'long',
  }).format(dateObj);
};

/**
 * Format day of week (e.g., "Monday")
 */
export const formatDayOfWeek = (date: string | Date): string => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return '';

  return new Intl.DateTimeFormat('en-IN', {
    weekday: 'long',
  }).format(dateObj);
};

/**
 * Truncate text with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length (default: 50)
 * @returns Truncated text
 */
export const truncateText = (text: string, maxLength: number = 50): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Capitalize first letter of each word
 */
export const capitalizeWords = (text: string): string => {
  if (!text) return '';
  return text
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Convert camelCase or snake_case to Title Case
 */
export const toTitleCase = (text: string): string => {
  if (!text) return '';

  // Replace underscores and split camelCase
  return text
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Format boolean to Yes/No
 */
export const formatBoolean = (value: boolean): string => {
  return value ? 'Yes' : 'No';
};

/**
 * Format weight (grams to appropriate unit)
 */
export const formatWeight = (grams: number): string => {
  if (grams === null || grams === undefined) return '0 g';

  if (grams < 1000) {
    return `${Math.round(grams)} g`;
  }

  const kilograms = grams / 1000;

  if (kilograms < 1000) {
    return `${kilograms.toFixed(2)} kg`;
  }

  const tonnes = kilograms / 1000;
  return `${tonnes.toFixed(2)} t`;
};

/**
 * Format date-time for tables (DD-MM-YYYY HH:MM AM/PM)
 * Used in list/table views for consistent date-time display
 * @param dateString - Date string or null/undefined
 * @returns Formatted date-time string or "-" if invalid
 */
export const formatDateTimeForTable = (dateString: string | Date | null | undefined): string => {
  if (!dateString) return '-';
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    if (isNaN(date.getTime())) return '-';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    const hoursStr = String(hours).padStart(2, '0');

    return `${day}-${month}-${year} ${hoursStr}:${minutes} ${ampm}`;
  } catch {
    return '-';
  }
};

/**
 * Format date for activity/logs display with relative time for recent and full date for older
 * Shows "Just now", "X min ago", "X hours ago", "X days ago" for recent
 * Shows full date-time for entries older than 7 days
 * @param dateString - Date string
 * @returns Formatted relative/absolute date string
 */
export const formatDateForLogs = (dateString: string): string => {
  if (!dateString) return '';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  // Relative time for recent changes
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  // Full date for older changes
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format date with full weekday (e.g., "Monday, 18 January 2026")
 * Used for attendance modals and detailed date displays
 */
export const formatDateFull = (dateStr: string): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';

  return date.toLocaleDateString('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

/**
 * Format date with time in 12-hour format (e.g., "18 Jan 2026, 10:30 AM")
 * Used for expense details, order details, etc.
 */
export const formatDateTimeDisplay = (dateStr: string): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';

  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

// ============================================================================
// TIMEZONE-AWARE FORMATTERS
// ============================================================================

/**
 * Format date to DD/MM/YYYY in specified timezone
 * @param date - Date string or Date object (UTC)
 * @param timezone - IANA timezone string (e.g., 'Asia/Kolkata')
 * @returns Formatted date string in specified timezone
 */
export const formatDateWithTimezone = (
  date: string | Date | null | undefined,
  timezone: string = 'UTC'
): string => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';

    return formatInTimeZone(dateObj, timezone, 'dd/MM/yyyy');
  } catch {
    return '';
  }
};

/**
 * Format time to HH:MM AM/PM in specified timezone
 * @param date - Date string or Date object (UTC)
 * @param timezone - IANA timezone string (e.g., 'Asia/Kolkata')
 * @returns Formatted time string in specified timezone
 */
export const formatTimeWithTimezone = (
  date: string | Date | null | undefined,
  timezone: string = 'UTC'
): string => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';

    return formatInTimeZone(dateObj, timezone, 'hh:mm a');
  } catch {
    return '';
  }
};

/**
 * Format date and time in specified timezone
 * @param date - Date string or Date object (UTC)
 * @param timezone - IANA timezone string (e.g., 'Asia/Kolkata')
 * @returns Formatted date-time string in specified timezone
 */
export const formatDateTimeWithTimezone = (
  date: string | Date | null | undefined,
  timezone: string = 'UTC'
): string => {
  if (!date) return '-';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '-';

    return formatInTimeZone(dateObj, timezone, 'dd MMM yyyy, hh:mm a');
  } catch {
    return '-';
  }
};

/**
 * Format date with full weekday in specified timezone
 * @param date - Date string or Date object (UTC)
 * @param timezone - IANA timezone string (e.g., 'Asia/Kolkata')
 * @returns Formatted date string like "Monday, 18 January 2026"
 */
export const formatDateFullWithTimezone = (
  date: string | Date | null | undefined,
  timezone: string = 'UTC'
): string => {
  if (!date) return '-';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '-';

    return formatInTimeZone(dateObj, timezone, 'EEEE, d MMMM yyyy');
  } catch {
    return '-';
  }
};

/**
 * Format date-time for tables in specified timezone (DD-MM-YYYY HH:MM AM/PM)
 * @param date - Date string or Date object (UTC)
 * @param timezone - IANA timezone string (e.g., 'Asia/Kolkata')
 * @returns Formatted date-time string
 */
export const formatDateTimeForTableWithTimezone = (
  date: string | Date | null | undefined,
  timezone: string = 'UTC'
): string => {
  if (!date) return '-';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '-';

    return formatInTimeZone(dateObj, timezone, 'dd-MM-yyyy hh:mm a');
  } catch {
    return '-';
  }
};

/**
 * Get current date string in specified timezone (YYYY-MM-DD)
 * @param timezone - IANA timezone string (e.g., 'Asia/Kolkata')
 * @returns Current date string in specified timezone
 */
export const getCurrentDateInTimezone = (timezone: string = 'UTC'): string => {
  try {
    const now = new Date();
    return formatInTimeZone(now, timezone, 'yyyy-MM-dd');
  } catch {
    return new Date().toISOString().split('T')[0];
  }
};

/**
 * Convert a date selected in org timezone to UTC for API submission
 * @param date - Date from date picker (treated as org timezone)
 * @param timezone - IANA timezone string (e.g., 'Asia/Kolkata')
 * @param endOfDay - If true, use end of day (23:59:59), else start of day (00:00:00)
 * @returns UTC Date object
 */
export const convertToUTC = (
  date: string | Date,
  timezone: string = 'UTC',
  endOfDay: boolean = false
): Date => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Parse date as if it's in the specified timezone
    const year = dateObj.getFullYear();
    const month = dateObj.getMonth();
    const day = dateObj.getDate();

    // Create date at start or end of day in timezone
    const hours = endOfDay ? 23 : 0;
    const minutes = endOfDay ? 59 : 0;
    const seconds = endOfDay ? 59 : 0;
    const ms = endOfDay ? 999 : 0;

    // Create a date string in the target timezone
    const tzDateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;

    // Convert from timezone to UTC
    return fromZonedTime(tzDateStr, timezone);
  } catch {
    return new Date();
  }
};

/**
 * Get today's date range in UTC based on organization timezone
 * @param timezone - IANA timezone string (e.g., 'Asia/Kolkata')
 * @returns Object with start (00:00:00) and end (23:59:59) in UTC
 */
export const getTodayRangeInUTC = (
  timezone: string = 'UTC'
): { start: Date; end: Date } => {
  try {
    const now = new Date();
    const todayStr = formatInTimeZone(now, timezone, 'yyyy-MM-dd');

    return {
      start: fromZonedTime(`${todayStr}T00:00:00.000`, timezone),
      end: fromZonedTime(`${todayStr}T23:59:59.999`, timezone),
    };
  } catch {
    const now = new Date();
    return { start: now, end: now };
  }
};

/**
 * Check if a date is today in the specified timezone
 * @param date - Date to check (UTC)
 * @param timezone - IANA timezone string
 * @returns Boolean
 */
export const isTodayInTimezone = (
  date: string | Date,
  timezone: string = 'UTC'
): boolean => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return false;

    const dateStr = formatInTimeZone(dateObj, timezone, 'yyyy-MM-dd');
    const todayStr = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');

    return dateStr === todayStr;
  } catch {
    return false;
  }
};

/**
 * Check if a date is in the past (before today in org timezone)
 * @param date - Date to check (UTC)
 * @param timezone - IANA timezone string
 * @returns Boolean
 */
export const isPastInTimezone = (
  date: string | Date,
  timezone: string = 'UTC'
): boolean => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return false;

    const dateStr = formatInTimeZone(dateObj, timezone, 'yyyy-MM-dd');
    const todayStr = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');

    return dateStr < todayStr;
  } catch {
    return false;
  }
};

/**
 * Check if a date is in the future (after today in org timezone)
 * @param date - Date to check (UTC)
 * @param timezone - IANA timezone string
 * @returns Boolean
 */
export const isFutureInTimezone = (
  date: string | Date,
  timezone: string = 'UTC'
): boolean => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return false;

    const dateStr = formatInTimeZone(dateObj, timezone, 'yyyy-MM-dd');
    const todayStr = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');

    return dateStr > todayStr;
  } catch {
    return false;
  }
};
